<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from '@heroicons/vue/24/outline'
import type { VNode } from 'vue'
import { computed } from 'vue'

export interface UiAlertBoxProps {
  type?: AlertBoxType
  showIcon?: boolean
  title?: string | null
}

interface Slots {
  default(): VNode[]
}

export type AlertBoxType = 'success' | 'error' | 'warning' | 'info' | 'standard'

const CLASSES_BY_TYPE: Record<AlertBoxType, string> = {
  success: 'border-success bg-success/20 text-success-content dark:text-success',
  error: 'border-error bg-error/20 text-error-content',
  warning: 'border-warning bg-warning/10 text-warning-content dark:text-warning',
  info: 'border-info bg-info/20',
  standard: 'border-gray-400 bg-gray-400/20',
}

const props = withDefaults(defineProps<UiAlertBoxProps>(), {
  type: 'standard',
  showIcon: false,
  title: null,
})
defineSlots<Slots>()

const iconComponent = computed(
  () =>
    ({
      success: CheckCircleIcon,
      error: ShieldExclamationIcon,
      warning: ExclamationTriangleIcon,
      info: ExclamationCircleIcon,
      standard: null,
    })[props.type]
)
</script>

<template>
  <div
    class="flex flex-1 items-stretch gap-4 rounded-lg border p-4"
    :class="CLASSES_BY_TYPE[type]"
  >
    <component
      :is="iconComponent"
      v-if="showIcon && iconComponent"
      class="w-4"
    />

    <div class="flex flex-col gap-1">
      <div
        v-if="title"
        class="text-xs font-semibold"
      >
        {{ title }}
      </div>

      <div class="flex h-full items-center gap-2 text-sm">
        <slot />
      </div>
    </div>
  </div>
</template>
